import { useState, useEffect, useCallback } from 'react';
import { collection, query, orderBy, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { resolveDispute as resolveDisputeApi } from '../lib/api';

/**
 * Hook for admin dispute management
 *
 * Fetches all disputes for admin review and provides resolution actions.
 */
export function useAdminDisputes() {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Subscribe to all disputes
  useEffect(() => {
    setLoading(true);

    const disputesRef = collection(db, 'disputes');
    const q = query(disputesRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const disputeList = [];

        for (const docSnapshot of snapshot.docs) {
          const data = docSnapshot.data();

          // Get raffle details
          let raffleTitle = 'Unknown Competition';
          try {
            const raffleDoc = await getDoc(doc(db, 'raffles', data.raffleId));
            if (raffleDoc.exists()) {
              raffleTitle = raffleDoc.data().title;
            }
          } catch (e) {
            console.error('Error fetching raffle for dispute:', e);
          }

          disputeList.push({
            id: docSnapshot.id,
            ...data,
            raffleTitle,
            createdAt: data.createdAt?.toDate?.() || new Date(),
            respondedAt: data.respondedAt?.toDate?.() || null,
            resolvedAt: data.resolvedAt?.toDate?.() || null,
          });
        }

        setDisputes(disputeList);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching disputes:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Resolve a dispute
  const resolve = useCallback(async (disputeId, resolution, adminNotes) => {
    try {
      const result = await resolveDisputeApi({
        disputeId,
        resolution,
        adminNotes,
      });
      return result;
    } catch (err) {
      console.error('Error resolving dispute:', err);
      throw err;
    }
  }, []);

  // Filter disputes by status
  const openDisputes = disputes.filter((d) => d.status === 'open');
  const underReviewDisputes = disputes.filter((d) => d.status === 'underReview');
  const resolvedDisputes = disputes.filter((d) => d.status === 'resolved');

  return {
    disputes,
    openDisputes,
    underReviewDisputes,
    resolvedDisputes,
    loading,
    error,
    resolve,
  };
}

/**
 * Hook for fetching a single dispute with full details
 */
export function useAdminDisputeDetail(disputeId) {
  const [dispute, setDispute] = useState(null);
  const [raffle, setRaffle] = useState(null);
  const [raiser, setRaiser] = useState(null);
  const [responder, setResponder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolving, setResolving] = useState(false);

  // Fetch dispute and related data
  useEffect(() => {
    if (!disputeId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const disputeRef = doc(db, 'disputes', disputeId);

    const unsubscribe = onSnapshot(
      disputeRef,
      async (docSnapshot) => {
        if (!docSnapshot.exists()) {
          setError('Dispute not found');
          setLoading(false);
          return;
        }

        const data = docSnapshot.data();
        setDispute({
          id: docSnapshot.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          respondedAt: data.respondedAt?.toDate?.() || null,
          resolvedAt: data.resolvedAt?.toDate?.() || null,
        });

        // Fetch related data
        try {
          // Raffle
          const raffleDoc = await getDoc(doc(db, 'raffles', data.raffleId));
          if (raffleDoc.exists()) {
            setRaffle({ id: raffleDoc.id, ...raffleDoc.data() });
          }

          // Raiser
          const raiserDoc = await getDoc(doc(db, 'users', data.raisedBy));
          if (raiserDoc.exists()) {
            setRaiser({ id: raiserDoc.id, ...raiserDoc.data() });
          }

          // Responder (against user)
          const responderDoc = await getDoc(doc(db, 'users', data.againstUser));
          if (responderDoc.exists()) {
            setResponder({ id: responderDoc.id, ...responderDoc.data() });
          }
        } catch (e) {
          console.error('Error fetching related data:', e);
        }

        setLoading(false);
      },
      (err) => {
        console.error('Error fetching dispute:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [disputeId]);

  // Resolve this dispute
  const resolve = useCallback(
    async (resolution, adminNotes) => {
      if (!disputeId) return;

      setResolving(true);
      try {
        const result = await resolveDisputeApi({
          disputeId,
          resolution,
          adminNotes,
        });
        return result;
      } catch (err) {
        console.error('Error resolving dispute:', err);
        throw err;
      } finally {
        setResolving(false);
      }
    },
    [disputeId]
  );

  return {
    dispute,
    raffle,
    raiser,
    responder,
    loading,
    error,
    resolve,
    resolving,
  };
}

export default useAdminDisputes;
